import { Box, Typography, IconButton, CircularProgress, TextField, Divider } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { TaDropZone } from './TaDropZone'
import { FancyButton } from './FancyButtons'
import { FS_SM, FS_XS, ICON_SM } from '../common/theme'
import { FileTypeIcon } from './FileTypeIcon'

const ACCEPTED = '.pdf,.docx,.xlsx,.txt'

interface BiqUploadPhaseProps {
  file: File | null
  setFile: (f: File | null) => void
  projectText: string
  setProjectText: (s: string) => void
  isExtracting: boolean
  isUploading: boolean
  extractStatus?: string
  onExtract: () => void
  onCancel: () => void
  handleFilePick: (files: FileList | null) => void
}

// Phase 1 of the bid-import wizard: pick a document to extract questions
// from, or paste raw question text and skip straight to the final import.
// The parent BidImportQuestionsModal advances to BiqReviewPhase once
// `onExtract` succeeds (text-only paste imports directly without review).
export default function BiqUploadPhase({
  file, setFile, projectText, setProjectText, isExtracting, isUploading,
  extractStatus, onExtract, onCancel, handleFilePick,
}: BiqUploadPhaseProps) {
  const busy = isExtracting || isUploading
  // Once extraction / text import starts the picker controls go away and
  // the panel collapses to a single progress card: the file name (or
  // "Text processing" for the paste path), a spinner, and the latest
  // status text streamed from the server.
  if (busy) {
    const label = file ? file.name : 'Text processing'
    return (
      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box data-testid="biq_processing" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
          {file ? <FileTypeIcon name={file.name} /> : null}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography data-testid="biq_processing_label" variant="body2" sx={{ fontSize: FS_SM, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {label}
            </Typography>
            {extractStatus ? (
              <Typography data-testid="biq_extract_status" variant="caption" sx={{ fontSize: FS_XS, opacity: 0.7 }}>
                {extractStatus}
              </Typography>
            ) : null}
          </Box>
          <CircularProgress size={20} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
          <FancyButton testId="biq_upload_cancel_btn" onClick={onCancel}>Cancel</FancyButton>
        </Box>
      </Box>
    )
  }
  return (
    <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>

      {!file && (
        <TaDropZone
          testId="biq_dropzone"
          inputTestId="biq_fileinput"
          accept={ACCEPTED}
          onFiles={handleFilePick}
          label="Drop a file here or click to browse"
          subLabel="PDF, DOCX, XLSX, TXT"
          sx={{ py: 3 }}
        />
      )}

      {file && (
        <Box data-testid="biq_filerow" sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <FileTypeIcon name={file.name} />
          <Typography data-testid="biq_filename" variant="caption" sx={{ flex: 1, fontSize: FS_SM, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {file.name}
          </Typography>
          <IconButton data-testid="biq_file_clear_btn" size="small" onClick={() => setFile(null)}>
            <CloseIcon sx={{ fontSize: ICON_SM }} />
          </IconButton>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Divider sx={{ flex: 1 }} />
        <Typography variant="caption" sx={{ fontSize: FS_XS, opacity: 0.5 }}>OR</Typography>
        <Divider sx={{ flex: 1 }} />
      </Box>

      <Typography variant="caption" sx={{ fontSize: FS_XS, opacity: 0.7 }}>
        Paste questions directly, separated by a blank line
      </Typography>
      <TextField
        multiline
        minRows={4}
        maxRows={10}
        fullWidth
        size="small"
        value={projectText}
        onChange={e => setProjectText(e.target.value)}
        placeholder="Question 1...&#10;&#10;Question 2..."
        inputProps={{ 'data-testid': 'biq_text_input' }}
        sx={{ '& .MuiInputBase-input': { fontSize: FS_SM } }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
        <FancyButton testId="biq_upload_cancel_btn" onClick={onCancel}>Cancel</FancyButton>
        <FancyButton
          testId="biq_extract_btn"
          solid
          onClick={onExtract}
          disabled={!file && !projectText.trim()}
        >
          {file ? 'Extract Questions' : 'Import Text'}
        </FancyButton>
      </Box>
    </Box>
  )
}
